import { useCallback, useContext } from 'react';
import { type Tag } from '@/lib/types';
import PortfolioItemContext from '../../../../../../PortfolioItemContext';
import { Checkbox } from '@mui/material';
import useComplexState from '@/lib/utils/useComplexState';
import TagList from './TagList';
import ClickableTag from './ClickableTag';
import SelectorPopover from './SelectorPopover';
import useBlockPortfolioItemCloseWhenDirty from '@/components/ChallengePortfolio/NormalView/portfolio-item/useBlockPortfolioItemCloseWhenDirty';

type TagSelectorProps = {
  id: string;
  anchorElement: HTMLButtonElement | null;
  allTags: Record<number, Tag>;
  onSave: (selectedTags: number[]) => Promise<void>;
  onClose: () => void;
};

const TagSelector = ({
  id,
  anchorElement,
  allTags,
  onSave,
  onClose,
}: TagSelectorProps) => {
  const { portfolioItem } = useContext(PortfolioItemContext);

  const [selectedTags, setSelectedTags] = useComplexState<Map<number, boolean>>(
    new Map(),
  );

  const toggleTagSelection = (tagId: number): void => {
    setSelectedTags(tags => {
      if (selectedTags.has(tagId)) {
        tags.delete(tagId);
      } else {
        tags.set(tagId, true);
      }
    });
  };

  const handleSave = useCallback(async (): Promise<void> => {
    if (selectedTags.size === 0) {
      onClose();

      return;
    }

    const selectedTagIds = Array.from(selectedTags.keys());

    await onSave(selectedTagIds);

    setSelectedTags(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/use-memo
  }, [JSON.stringify(Array.from(selectedTags.keys()))]);

  useBlockPortfolioItemCloseWhenDirty(selectedTags.size > 0, handleSave);

  const alreadyAttachedTags = portfolioItem.tags.map(tag => tag.id);

  return (
    <SelectorPopover
      id={anchorElement !== null ? id : undefined}
      open={anchorElement !== null}
      anchorEl={anchorElement}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      slotProps={{
        transition: {
          onExit: handleSave,
        },
      }}
    >
      <TagList>
        {Object.values(allTags ?? {})
          .filter(tag => !alreadyAttachedTags.includes(tag.id))
          .map(tag => (
            <li key={tag.id}>
              <Checkbox
                checked={selectedTags.has(tag.id)}
                onChange={() => toggleTagSelection(tag.id)}
              />
              <ClickableTag
                tagColor={tag.color}
                onClick={() => toggleTagSelection(tag.id)}
              >
                <span>{tag.name}</span>
              </ClickableTag>
            </li>
          ))}
      </TagList>
    </SelectorPopover>
  );
};

export default TagSelector;
